/**
 * Винительный падеж названия приза: «Яндекс Станция» → «Яндекс Станцию».
 *
 * Нужен фразе из макета «Среди топ-10 разыграем …»: название лежит в базе
 * в именительном («Яндекс Станция»), а глагол требует винительного.
 * Оператор вводит название один раз, и в других местах (список победителей,
 * админка) именительный правильный — значит склонять надо на показе.
 *
 * ЧТО ЭТО НЕ ДЕЛАЕТ. Полного морфологического разбора здесь нет и быть
 * не может: словарь вроде pymorphy в бандл мини-аппа не положишь. Правило
 * покрывает единственный случай, в котором винительный отличается от
 * именительного у неодушевлённых, — женский род на «-а/-я». Всё остальное
 * (мужской и средний род, множественное число, латиница, аббревиатуры)
 * в винительном совпадает с именительным, и мы его не трогаем.
 *
 * Поэтому неверный результат возможен ровно там, где слово выглядит
 * женским, но им не является. Цена ошибки — одна буква в окончании,
 * цена отказа — «разыграем Яндекс Станция» в каждом цикле.
 */

/** Предлог обрывает именную группу: дальше склонять уже нечего. */
const PREPOSITIONS = new Set([
  'на', 'в', 'во', 'с', 'со', 'по', 'из', 'от', 'до', 'к', 'ко', 'о', 'об',
  'при', 'за', 'над', 'под', 'у', 'без', 'про', 'через', 'для',
]);

/**
 * Служебные слова, которые выглядят женским родом, но им не являются.
 * «для» здесь и как предлог, и как ловушка: без списка вышло бы «длю».
 */
const NOT_A_NOUN = new Set(['для', 'она', 'моя', 'твоя', 'вся', 'два', 'три']);

/** Окончания женского рода → винительный. Порядок важен: длинные первыми. */
const ENDINGS: readonly [string, string][] = [
  ['ая', 'ую'],
  ['яя', 'юю'],
  ['ья', 'ью'],
  ['ия', 'ию'],
  ['я', 'ю'],
  ['а', 'у'],
];

const CYRILLIC_WORD = /^[а-яё]+(-[а-яё]+)?$/i;

function isDeclinable(word: string): boolean {
  const lower = word.toLowerCase();
  if (NOT_A_NOUN.has(lower)) return false;
  // Аббревиатуры («ФИФА», «УЕФА») не склоняются, латиница — тем более
  if (word.length < 3 || word === word.toUpperCase()) return false;
  if (!CYRILLIC_WORD.test(word)) return false;
  return ENDINGS.some(([from]) => lower.endsWith(from));
}

function declineWord(word: string): string {
  const lower = word.toLowerCase();
  for (const [from, to] of ENDINGS) {
    if (lower.endsWith(from)) {
      return word.slice(0, word.length - from.length) + to;
    }
  }
  return word;
}

const IS_ADJECTIVE = /(ая|яя)$/i;

export function toAccusative(phrase: string): string {
  const words = phrase.split(' ');

  for (let i = 0; i < words.length; i++) {
    const word = words[i] ?? '';
    // Именная группа кончилась — дальше идёт зависимая часть
    // («Поездка на матч»), и склонять в ней нечего
    if (PREPOSITIONS.has(word.toLowerCase())) break;
    if (!isDeclinable(word)) continue;

    const wasAdjective = IS_ADJECTIVE.test(word);
    words[i] = declineWord(word);
    if (!wasAdjective) break;

    // Прилагательное склоняется вместе со своим существительным:
    // «Умная колонка» → «Умную колонку»
    for (let j = i + 1; j < words.length; j++) {
      const next = words[j] ?? '';
      if (PREPOSITIONS.has(next.toLowerCase())) break;
      if (!isDeclinable(next)) continue;
      words[j] = declineWord(next);
      break;
    }
    break;
  }

  return words.join(' ');
}
